/**
 * PnL and PnL% Calculator for Trading Terminal
 * Calculates PnL ($) and PnL (%) dynamically based on entry, exit, FTMO Lots,
 * side/scenario, and exact FTMO broker specifications from the Position Calculator.
 */

// FTMO CFD Contract Sizes & Dollar-per-point multiplier per 1.0 Lot
export const FTMO_ASSET_RULES = {
    // Forex (1.0 Lot = 100,000 units = $10/pip for USD quote)
    'EURUSD': { contractSize: 100000, quoteCurrency: 'USD' },
    'GBPUSD': { contractSize: 100000, quoteCurrency: 'USD' },
    'AUDUSD': { contractSize: 100000, quoteCurrency: 'USD' },
    'USDCHF': { contractSize: 100000, quoteCurrency: 'USD' },
    'USDCAD': { contractSize: 100000, quoteCurrency: 'USD' },
    'NZDUSD': { contractSize: 100000, quoteCurrency: 'USD' },
    'USDJPY': { contractSize: 100000, quoteCurrency: 'JPY' },
    'EURJPY': { contractSize: 100000, quoteCurrency: 'JPY' },
    'GBPJPY': { contractSize: 100000, quoteCurrency: 'JPY' },
    
    // Commodities
    'XAUUSD': { contractSize: 100, quoteCurrency: 'USD' },      // 1.0 Lot = 100 oz ($1 move = $100)
    'GOLD':   { contractSize: 100, quoteCurrency: 'USD' },
    'XAGUSD': { contractSize: 5000, quoteCurrency: 'USD' },     // 1.0 Lot = 5000 oz ($1 move = $5,000)
    'SILVER': { contractSize: 5000, quoteCurrency: 'USD' },
    'USOIL':  { contractSize: 100, quoteCurrency: 'USD' },      // 1.0 Lot = 100 barrels ($1 move = $100)
    'OIL':    { contractSize: 100, quoteCurrency: 'USD' },
    'BRENT':  { contractSize: 100, quoteCurrency: 'USD' },

    // Indices (FTMO CFD: 1.0 Lot = 100 contracts = $100 per index point)
    'S&P500':       { contractSize: 100, quoteCurrency: 'USD' },
    'SPX500':       { contractSize: 100, quoteCurrency: 'USD' },
    'US500':        { contractSize: 100, quoteCurrency: 'USD' },
    'NASDAQ':       { contractSize: 100, quoteCurrency: 'USD' },
    'NAS100':       { contractSize: 100, quoteCurrency: 'USD' },
    'US100':        { contractSize: 100, quoteCurrency: 'USD' },
    'US30':         { contractSize: 100, quoteCurrency: 'USD' },
    'DJ30':         { contractSize: 100, quoteCurrency: 'USD' },
    'RUSSELL 2000': { contractSize: 100, quoteCurrency: 'USD' },
    'US2000':       { contractSize: 100, quoteCurrency: 'USD' },
    'USDX':         { contractSize: 100, quoteCurrency: 'USD' },
    'EURX':         { contractSize: 100, quoteCurrency: 'USD' },
    'JPYX':         { contractSize: 100, quoteCurrency: 'USD' },
    'GER30':        { contractSize: 100, quoteCurrency: 'EUR' },
    'GER40':        { contractSize: 100, quoteCurrency: 'EUR' },

    // Crypto (FTMO CFD: contractSize = 100)
    'BTCUSD': { contractSize: 100, quoteCurrency: 'USD' },
    'ETHUSD': { contractSize: 100, quoteCurrency: 'USD' },
};

export const getFtmoRule = (assetSymbol) => {
    const sym = String(assetSymbol || '').toUpperCase().trim();
    if (FTMO_ASSET_RULES[sym]) {
        return FTMO_ASSET_RULES[sym];
    }

    // Fallbacks by asset class
    if (sym.includes('JPY')) {
        return { contractSize: 100000, quoteCurrency: 'JPY' };
    }
    if (sym.includes('XAU') || sym.includes('GOLD')) {
        return { contractSize: 100, quoteCurrency: 'USD' };
    }
    if (sym.includes('XAG') || sym.includes('SILVER')) {
        return { contractSize: 5000, quoteCurrency: 'USD' };
    }
    if (sym.includes('OIL')) {
        return { contractSize: 100, quoteCurrency: 'USD' };
    }
    if (sym.includes('BTC') || sym.includes('ETH')) {
        return { contractSize: 100, quoteCurrency: 'USD' };
    }
    const isIndices = ["US30", "US100", "US500", "GER30", "NAS100", "US2000", "S&P", "SPX", "NASDAQ", "RUSSELL"].some(k => sym.includes(k));
    if (isIndices) {
        return { contractSize: 100, quoteCurrency: 'USD' };
    }
    const isForex = sym.length === 6 && !["XAU", "XAG", "OIL"].some(k => sym.includes(k));
    if (isForex) {
        return { contractSize: 100000, quoteCurrency: 'USD' };
    }

    // Default FTMO fallback
    return { contractSize: 100, quoteCurrency: 'USD' };
};

export const calculateTradePnL = (trade) => {
    if (!trade) return { pnl: null, pnlPercent: null };

    const entryStr = String(trade.entry !== undefined && trade.entry !== null ? trade.entry : '').replace(/[^0-9.-]/g, '');
    const exitStr = String(trade.exit !== undefined && trade.exit !== null ? trade.exit : '').replace(/[^0-9.-]/g, '');

    const entry = parseFloat(entryStr);
    const exit = parseFloat(exitStr);

    // Both entry and exit must be valid positive numbers
    if (isNaN(entry) || isNaN(exit) || entry <= 0 || exit <= 0) {
        return { pnl: null, pnlPercent: null };
    }

    // Determine trade direction: LONG (default) or SHORT
    const rawSide = String(
        trade.side || 
        (trade.scenario === 'Bullish' ? 'LONG' : trade.scenario === 'Bearish' ? 'SHORT' : 'LONG')
    ).toUpperCase();
    
    const isShort = rawSide.includes('SHORT') || rawSide.includes('SELL') || rawSide.includes('BEAR');
    const direction = isShort ? -1 : 1;

    // 1. Percentage PnL (%): ((Exit - Entry) / Entry) * 100 * direction
    const priceDiff = (exit - entry) * direction;
    const pct = (priceDiff / entry) * 100;
    const formattedPct = `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`;

    // 2. Parse Position Size (Always in FTMO Lots)
    const sizeStr = String(trade.size !== undefined && trade.size !== null ? trade.size : '').trim();
    const rawLots = parseFloat(sizeStr.replace(/[^0-9.-]/g, ''));
    const lots = (!isNaN(rawLots) && rawLots > 0) ? rawLots : 1.0;

    // 3. FTMO Asset Rule
    const rule = getFtmoRule(trade.asset);

    // 4. Monetary PnL ($) using FTMO Contract Size:
    // PnL = priceDiff * lots * contractSize
    let pnlAmount = priceDiff * lots * rule.contractSize;

    // Convert JPY quotes to USD if needed
    if (rule.quoteCurrency === 'JPY') {
        pnlAmount = pnlAmount / (exit > 0 ? exit : 150);
    }

    const formattedPnl = `${pnlAmount >= 0 ? '+' : ''}${Math.abs(pnlAmount) >= 100 ? Math.round(pnlAmount) : pnlAmount.toFixed(2)}`;

    return {
        pnl: formattedPnl,
        pnlPercent: formattedPct
    };
};
